﻿import * as eventTypes from '../utils/eventTypes';
import * as styleTypes from '../utils/style/styleTypes';
import * as geomTypes from '../utils/geomTypes';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { createOlStyleFunction } from '../utils/style/canvas/OlStyler';
import Modify from 'ol/interaction/Modify';
import Draw from 'ol/interaction/Draw';
import XTranslate from '../interaction/XTranslate';

export default class TemporaryEditHandler {
    constructor(mapId, map, layerHandler, selectionHandler, classId) {
        this.mapId = mapId;
        this.map = map;
        this.layerHandler = layerHandler;
        this.selectionHandler = selectionHandler;
        this.insertInteraction = null;
        this.editInteraction = null;
        this.moveInteraction = null;
        this.editSource = null;
        this.editLayer = null;
        this.features = [];
        this.layerSettings = null;
        this.style = null;
        this.classId = classId;
        this._registerEventHandlers();
    }

    _registerEventHandlers() {
        $event.register(eventTypes.FEATURE_EDIT_STARTED, this._getFeatureEditStartedHandler(), this, this.classId);
        $event.register(eventTypes.FEATURE_EDITOR_STARTED, this._getFeatureEditorStartedHandler(), this, this.classId);
        $event.register(eventTypes.FEATURE_MOVE_STARTED, this._getFeatureMoveStartedHandler(), this, this.classId);
        $event.register(eventTypes.FEATURE_EDITOR_CANCELED, this._getFeatureMoveCanceledHandler(), this, this.classId);
        $event.register(eventTypes.FEATURE_REMOVED, this._getFeatureRemovedHandler(), this, this.classId);
    }

    _getFeatureEditorStartedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null && data.features != null) {
                this._startEditor(data.features, data.layerId);
            }
        };

        return handler;
    }

    _getFeatureEditStartedHandler() {
        var handler = (eventName, data, sender) => {
            this._startEdit();
        };

        return handler;
    }

    _getFeatureMoveStartedHandler() {
        var handler = (eventName, data, sender) => {
            this._startMove();
        };

        return handler;
    }

    _getFeatureMoveCanceledHandler() {
        var handler = (eventName, data, sender) => {
            this._stopEditor();
        };

        return handler;
    }

    _getFeatureRemovedHandler() {
        var handler = (eventName, data, sender) => {
            this._removeFeatures();
        };

        return handler;
    }

    _removeFeatures() {
        this._clearAllInteractions();
        let features = this.editSource.getFeatures();
        this.editSource.removeFeatures(features);
        $event.notify(eventTypes.FEATURE_MODIFIED, { features: this.editSource.getFeatures() }, this, this.classId);
    }

    _stopEditor() {
        this.selectionHandler.enableSelection();
        this._clearAllInteractions();
        this.map.removeLayer(this.editLayer);
        this.editSource = null;
        this.editLayer = null;
        this.features = [];
        this.layerSettings = null;
        this.style = null;
    }

    _startEditor(features, layerId) {
        this.map.getViewport().style.cursor = '';
        this.selectionHandler.disableSelection();
        this._clearAllInteractions();

        let layer = this.layerHandler.getLayerById(layerId);

        if (layer[0].get('temporarySettings') != null) {
            this.layerSettings = layer[0].get('temporarySettings');
        }

        this.features = features;

        this._createEditLayer(this.layerSettings).then((style) => {
            this.style = style;
            features.forEach(feature => {
                feature.set('xstate', styleTypes.EDIT_STYLE_TYPE);
                let featureClone = feature.clone();
                featureClone.setId(feature.getId());
                this.editSource.addFeature(featureClone);
            });
        });
    }

    _startEdit() {
        this._clearAllInteractions();

        if (this.editSource.getFeatures().length > 0) {
            if (this.layerSettings.geometryType == geomTypes.GEOM_POINT) {
                this._startMove();
            } else {
                this.editInteraction = new Modify({
                    source: this.editSource,
                    style: this.style
                });

                this.editInteraction.on('modifyend', (evt) => {
                    $event.notify(eventTypes.FEATURE_MODIFIED, { features: this.editSource.getFeatures() }, this, this.classId);
                });

                this.map.addInteraction(this.editInteraction);
            }
        } else {
            this.map.getViewport().style.cursor = 'crosshair';
            this.insertInteraction = new Draw({
                source: this.editSource,
                type: this._getDrawingType(this.layerSettings.geometryType),
                style: this.style
            });

            this.insertInteraction.on('drawend', (evt) => {
                evt.feature.set('xstate', styleTypes.EDIT_STYLE_TYPE);

                $event.notify(eventTypes.FEATURE_MODIFIED, { features: [evt.feature] }, this, this.classId);
                this._clearInsert();
                this.map.getViewport().style.cursor = '';
            });

            this.map.addInteraction(this.insertInteraction);

            if (this.layerSettings.geometryType == geomTypes.GEOM_POINT) {
                this.moveInteraction = new XTranslate({
                    source: this.editSource,
                    style: this.style
                });

                this.moveInteraction.on('translateend', (evt) => {
                    $event.notify(eventTypes.FEATURE_MODIFIED, { features: this.editSource.getFeatures() }, this, this.classId);
                });

                this.map.addInteraction(this.moveInteraction);
            } else {
                this.editInteraction = new Modify({
                    source: this.editSource,
                    style: this.style
                });

                this.editInteraction.on('modifyend', (evt) => {
                    $event.notify(eventTypes.FEATURE_MODIFIED, { features: this.editSource.getFeatures() }, this, this.classId);
                });

                this.map.addInteraction(this.editInteraction);
            }
        }
    }

    _startMove() {
        this._clearAllInteractions();

        this.moveInteraction = new XTranslate({
            source: this.editSource,
            style: this.style
        });

        this.moveInteraction.on('translateend', (evt) => {
            $event.notify(eventTypes.FEATURE_MODIFIED, { features: this.editSource.getFeatures() }, this, this.classId);
        });

        this.map.addInteraction(this.moveInteraction);
    }

    _clearAllInteractions() {
        this._clearInsert();
        this._clearEdit();
        this._clearMove();
    }

    _clearInsert() {
        if (this.insertInteraction) {
            this.map.removeInteraction(this.insertInteraction);
            this.insertInteraction = null;
        }
    }

    _clearEdit() {
        if (this.editInteraction) {
            this.map.removeInteraction(this.editInteraction);
            this.editInteraction = null;
        }
    }

    _clearMove() {
        if (this.moveInteraction) {
            this.map.removeInteraction(this.moveInteraction);
            this.moveInteraction = null;
        }
    }

    _createEditLayer(layerSettings) {
        return new Promise((resolve, reject) => {
            let styleId = null;

            switch (layerSettings.geometryType) {
                case geomTypes.GEOM_POINT: styleId = styleTypes.SYSTEM_STYLE_EDIT_POINT;
                    break;
                case geomTypes.GEOM_LINE: styleId = styleTypes.SYSTEM_STYLE_EDIT_LINE;
                    break;
                case geomTypes.GEOM_POLYGON: styleId = styleTypes.SYSTEM_STYLE_EDIT_POLYGON;
                    break;
                default: styleId = styleTypes.SYSTEM_STYLE_EDIT_ALL;
                    break;
            }

            this.layerHandler.styleHandler.loadStyle(styleId, true).then(() => {
                let style = this.layerHandler.styleHandler.getCanvasStyleById(styleId, styleTypes.EDIT_STYLE_TYPE);

                if (this.editSource == null) {
                    this.editSource = new VectorSource({});
                }

                let styleFunction = (feature) => {
                    let styleGetter = createOlStyleFunction(style.style, this.map);
                    return styleGetter(feature);
                }

                let layerOptions = {
                    uid: 'editLayer',
                    source: this.editSource,
                    style: styleFunction,
                    zIndex: 10000,
                    visible: true
                };

                this.editLayer = new VectorLayer(layerOptions);
                this.map.addLayer(this.editLayer);
                resolve(styleFunction);
            });
        });
    }

    _getDrawingType(geometryType) {
        switch (geometryType) {
            case geomTypes.GEOM_POINT: return "Point";
            case geomTypes.GEOM_LINE: return "LineString";
            case geomTypes.GEOM_POLYGON: return "Polygon";
            default: throw 'Geometry not supported';
        }
    }
}